import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ExternalLink } from 'lucide-react';
import { isImageReceiptUrl, isPdfReceiptUrl } from '../../utils/paymentReceipt';

interface PaymentReceiptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiptUrl: string | null;
  title?: string;
  subtitle?: string;
}

const PaymentReceiptPreviewModal: React.FC<PaymentReceiptPreviewModalProps> = ({
  isOpen,
  onClose,
  receiptUrl,
  title = 'Comprobante de pago',
  subtitle,
}) => {
  if (!receiptUrl) return null;

  const isImage = isImageReceiptUrl(receiptUrl);
  const isPdf = isPdfReceiptUrl(receiptUrl);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[110] w-full max-w-3xl max-h-[90vh] rounded-xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">{title}</Dialog.Title>
              {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 min-h-[200px]">
            {isImage ? (
              <img src={receiptUrl} alt="Comprobante de pago" className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm" />
            ) : isPdf ? (
              <iframe src={receiptUrl} title="Comprobante de pago" className="w-full h-[70vh] rounded-lg border border-gray-200 dark:border-gray-700 bg-white" />
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Vista previa no disponible para este formato.</p>
                <a
                  href={receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Abrir comprobante
                </a>
              </div>
            )}
          </div>
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end shrink-0">
            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir en nueva pestaña
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PaymentReceiptPreviewModal;
